'use client';

import {
  BadgeCheck,
  BookOpenText,
  ChevronLeft,
  ChevronRight,
  Search,
  Sparkles,
} from 'lucide-react';
import Link from 'next/link';
import { useMemo, useState } from 'react';

const BOOK_COVER_BASE = 'https://api.annajmacademy.com/static/images_optimize/books';

type Book = {
  id: string;
  titleId: string;
  titleAr?: string;
  coverImage?: string;
};

const books: Book[] = [
  {
    id: 'kosakata-di-sekolah',
    titleId: 'Kosakata di Sekolah',
    titleAr: 'مُفْرَدَاتُ الْمَدْرَسَةِ',
    coverImage: `${BOOK_COVER_BASE}/kosakata_sekolah.png`,
  },
  {
    id: 'Panduan Nahwu',
    titleId: 'Panduan Nahwu',
    titleAr: 'الْأُسْرَة',
    coverImage: `${BOOK_COVER_BASE}/1.png`,
  },
  {
    id: 'perkenalan-laki-laki',
    titleId: 'Perkenalan Laki-laki',
    titleAr: 'التَّعَارُف لِلْمُؤَنَّث',
    coverImage: `${BOOK_COVER_BASE}/2.png`,
  },
  {
    id: 'kosakata-tubuh',
    titleId: 'Kosakata Anggota Tubuh',
    titleAr: 'مُفْرَدَاتُ الْجَسَمِ',
    coverImage: `${BOOK_COVER_BASE}/kosakata_tubuh.png`,
  },
  {
    id: 'Dhomir munfasil',
    titleId: 'Dhomir Munfasil',
    coverImage: `${BOOK_COVER_BASE}/dhomir.png`,
  },
  {
    id: 'Sapaan',
    titleId: 'Sapaan',
    titleAr: 'الْأَثَاث',
    coverImage: `${BOOK_COVER_BASE}/sapaan.png`,
  }
];

const MOBILE_VISIBLE_BOOK_COUNT = 4;

function normalize(text: string) {
  return text.toLocaleLowerCase('id-ID');
}

export default function EbookPreview() {
  const [query, setQuery] = useState('');

  const filteredBooks = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return books;
    return books.filter((book) => {
      const combined = normalize(`${book.titleId} ${book.titleAr ?? ''}`);
      return combined.includes(q);
    });
  }, [query]);

  return (
    <div
      className="relative rounded-2xl border border-violet-100 bg-white p-3 shadow-xl ring-1 ring-violet-100/50 sm:rounded-3xl sm:p-5 lg:p-6"
      aria-label="Cuplikan halaman koleksi ebook Bahasa Arab"
    >
      <div className="flex items-center gap-1.5 sm:gap-2">
        <BookOpenText className="h-4 w-4 text-violet-600 sm:h-5 sm:w-5" aria-hidden />
        <h3 className="text-sm font-bold text-gray-900 sm:text-base">Books</h3>
      </div>

      {/* <div className="mt-3 rounded-xl bg-gradient-to-br from-violet-100 via-violet-50 to-fuchsia-50 p-2.5 sm:mt-4 sm:p-3.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-violet-700 shadow-sm sm:text-[11px]">
          <Sparkles className="h-3 w-3" aria-hidden />
          Konten Terbaik dari Ahlinya
        </span>
        <p className="mt-1.5 text-[10px] leading-snug text-gray-700 sm:mt-2 sm:text-[11px]">
          Diverifikasi oleh guru Bahasa Arab berpengalaman, disajikan dalam format ebook dan modul materi
          yang mudah dipahami.
        </p>
      </div> */}

      {/* <div className="mt-3 flex items-center gap-2 sm:mt-4">
        <label className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[10px] text-gray-500 focus-within:border-violet-300 focus-within:ring-1 focus-within:ring-violet-200 sm:gap-2 sm:px-2.5 sm:text-[11px]">
          <Search className="h-3 w-3 shrink-0 text-gray-400 sm:h-3.5 sm:w-3.5" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search books..."
            className="min-w-0 flex-1 bg-transparent text-gray-700 outline-none placeholder:text-gray-400"
            aria-label="Cari ebook"
          />
        </label>
        <div className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-violet-500 px-2 py-1.5 text-[10px] font-semibold text-white shadow-sm sm:px-2.5 sm:text-[11px]">
          <BadgeCheck className="h-3 w-3" aria-hidden />
          Pro Plan
        </div>
      </div> */}

      <div className="mt-3 flex items-center justify-between gap-2 sm:mt-4">
        <h4 className="line-clamp-1 text-xs font-bold text-gray-900 sm:text-sm md:text-base">
          Percakapan Bahasa Arab
        </h4>
        <div className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-gray-600 shadow-sm sm:text-[11px]">
          View All
          <ChevronRight className="h-3 w-3" aria-hidden />
        </div>
      </div>

      {filteredBooks.length === 0 ? (
        <div className="mt-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-6 text-center text-[11px] text-gray-500 sm:text-xs">
          Tidak ada ebook yang cocok dengan{' '}
          <span className="font-semibold text-gray-700">&ldquo;{query}&rdquo;</span>.
        </div>
      ) : (
        <div className="mt-2.5 grid grid-cols-2 gap-2 sm:mt-3 sm:grid-cols-3 sm:gap-2.5">
          {filteredBooks.map((book, index) => (
            <Link
              key={book.id}
              href="/login"
              className={`group block overflow-hidden rounded-lg border border-gray-200 bg-white transition hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-md ${
                index >= 4 ? 'hidden sm:block' : ''
              }`}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-b from-violet-100/70 to-violet-50/40">
                {book.coverImage ? (
                  <img
                    src={book.coverImage}
                    alt={book.titleId}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-2 text-center">
                    <BookOpenText
                      className="h-4 w-4 text-violet-600 sm:h-5 sm:w-5"
                      aria-hidden
                    />
                    <p className="line-clamp-2 text-[9px] font-medium leading-tight text-gray-700 sm:text-[10px]">
                      {book.titleId}
                    </p>
                    {book.titleAr ? (
                      <p
                        dir="rtl"
                        className="line-clamp-1 text-[9px] font-medium leading-tight text-gray-600 sm:text-[10px]"
                      >
                        {book.titleAr}
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between gap-1 border-t border-gray-100 px-1.5 py-1 sm:px-2 sm:py-1.5">
                <p className="truncate text-[10px] font-semibold text-gray-900 sm:text-[11px]">
                  {book.titleId}
                </p>
                <span className="shrink-0 text-[9px] font-semibold text-violet-600 transition group-hover:text-violet-700 sm:text-[10px]">
                  Open
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-100 pt-3 sm:mt-5 sm:pt-4">
        <Link
          href="/login"
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-gray-500 shadow-sm transition hover:bg-gray-50 sm:rounded-xl sm:px-3 sm:py-1.5 sm:text-xs"
        >
          <ChevronLeft className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
          Prev
        </Link>
        <span className="text-[10px] text-gray-500 sm:hidden sm:text-[11px]">
          {Math.min(MOBILE_VISIBLE_BOOK_COUNT, filteredBooks.length)} buku
        </span>
        <span className="hidden text-[10px] text-gray-500 sm:inline sm:text-[11px]">
          {filteredBooks.length} buku
        </span>
        <Link
          href="/login"
          className="inline-flex items-center gap-1 rounded-lg bg-violet-500 px-2 py-1 text-[10px] font-semibold text-white shadow-sm transition hover:opacity-95 sm:rounded-xl sm:px-3 sm:py-1.5 sm:text-xs"
        >
          Next
          <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
        </Link>
      </div>
    </div>
  );
}
